import { formatShortExcessLabel } from './dailyTransactionSectionRows';
import { buildFuelSalesNozzleSummaryFromDetailed } from './dailyTransactionFuelSalesRows';

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function groupRows(rows, keyFn, seedFn, mergeFn, finalizeFn = (row) => row) {
  const groups = new Map();

  (rows || []).forEach((row) => {
    const key = keyFn(row);
    const current = groups.get(key) || seedFn(row);
    mergeFn(current, row);
    groups.set(key, current);
  });

  return Array.from(groups.values()).map(finalizeFn);
}

export function buildSummaryPayloadFromDetailed(data) {
  const receiptsDetailed = [
    ...(data.reciept_voucher_detailed || []),
    ...(data.reciept_account_transfer_detailed || []),
    ...(data.reciept_salary_detailed || []),
  ];

  const paymentDetailed = [
    ...(data.payment_voucher_detailed || []),
    ...(data.payment_voucher_detailed_account_transfer || []),
  ];

  return {
    fuel_sales_summary: buildFuelSalesNozzleSummaryFromDetailed(
      data.fuel_sales_detailed,
      toNumber(data.opening_balance)
    ),
    loose_oil_sales_summary: groupRows(
      data.loose_oil_sales_detailed,
      (row) => `${row.product_name || ''}|${row.selling_price || 0}`,
      (row) => ({
        product_name: row.product_name || '',
        quantity: 0,
        selling_price: row.selling_price || '',
        dr_amount: 0,
        cr_amount: 0,
      }),
      (target, row) => {
        target.quantity += toNumber(row.quantity);
        target.dr_amount += toNumber(row.dr_amount);
        target.cr_amount += toNumber(row.cr_amount);
      }
    ),
    packed_oil_sales_summary: groupRows(
      data.packed_oil_sales_detailed,
      (row) => `${row.product_name || ''}|${row.selling_price || 0}`,
      (row) => ({
        product_name: row.product_name || '',
        quantity: 0,
        selling_price: row.selling_price || '',
        dr_amount: 0,
        cr_amount: 0,
      }),
      (target, row) => {
        target.quantity += toNumber(row.quantity);
        target.dr_amount += toNumber(row.dr_amount);
        target.cr_amount += toNumber(row.cr_amount);
      }
    ),
    reciept_voucher_summary: receiptsDetailed.map((row) => ({
      name: row.name || row.ac_name_slug || '',
      category: row.category || '',
      ac_name_slug: row.sub_cat || row.ac_name_slug || '',
      note: row.note || '',
      amount: toNumber(row.amount || row.dr_amount),
    })),
    other_income_summary: (data.other_income_detailed || []).map((row) => ({
      ac_name_slug: row.ac_name_slug || '',
      note: row.note || '',
      amount: toNumber(row.amount || row.dr_amount),
    })),
    credit_sales_summary: groupRows(
      data.credit_sales_detailed,
      (row) => `${row.name || 'New Customer'}|${row.fuel_name || ''}`,
      (row) => ({
        name: row.name || 'New Customer',
        fuel_name: row.fuel_name || '',
        quantity: 0,
        amount: 0,
      }),
      (target, row) => {
        target.quantity += toNumber(row.quantity);
        target.amount += toNumber(row.dr_amount ?? row.amount);
      }
    ),
    recoup_summary: groupRows(
      data.recoup_detailed,
      (row) => `${row.fuel_name || ''}|${row.tank_name || ''}`,
      (row) => ({
        fuel_name: row.fuel_name || '',
        tank_name: row.tank_name || '',
        quantity: 0,
        amount: 0,
      }),
      (target, row) => {
        target.quantity += toNumber(row.quantity);
        target.amount += toNumber(row.amount || row.creditAmount);
      }
    ),
    mla_coupon_summary: groupRows(
      data.mla_coupon_detailed,
      (row) => `${row.coupon_no || ''}|${row.fuel_name || ''}`,
      (row) => ({
        coupon_no: row.coupon_no || '',
        fuel_name: row.fuel_name || '',
        quantity: 0,
        amount: 0,
      }),
      (target, row) => {
        target.quantity += toNumber(row.quantity);
        target.amount += toNumber(row.amount || row.creditAmount);
      }
    ),
    fleet_card_summary: groupRows(
      data.fleet_card_detailed,
      (row) => `${row.card_no || ''}|${row.fuel_name || ''}`,
      (row) => ({
        card_no: row.card_no || '',
        fuel_name: row.fuel_name || '',
        quantity: 0,
        amount: 0,
      }),
      (target, row) => {
        target.quantity += toNumber(row.quantity);
        target.amount += toNumber(row.amount || row.creditAmount);
      }
    ),
    expense_voucher_summary: groupRows(
      data.expense_voucher_detailed,
      (row) => `${row.name || row.ac_name_slug || ''}|${row.category || ''}|${row.sub_cat || ''}`,
      (row) => ({
        party_name: row.name || row.party_name || row.ac_name_slug || '',
        category: row.category || '',
        sub_category: row.sub_cat || '',
        amount: 0,
      }),
      (target, row) => {
        target.amount += toNumber(row.amount || row.creditAmount);
      }
    ),
    payment_voucher_summary: paymentDetailed.map((row) => ({
      name: row.name || row.ac_name_slug || '',
      ac_name_slug: row.ac_name_slug || row.sub_cat || '',
      amount: toNumber(row.amount || row.creditAmount),
    })),
    digital_payment_summary: (data.digital_payment_detailed || []).map((row) => ({
      method_name: row.method_name || row.accounts_head || '',
      bank_name: row.bank_name || '',
      amount: toNumber(row.amount || row.creditAmount),
    })),
    short_excess_summary: (data.short_excess_detailed || data.short_excess_summary || []).map((row) => ({
      name: row.name || '',
      short_excess: formatShortExcessLabel(row),
      short_excess_amount: toNumber(row.short_amount || row.excess_amount || row.short_excess_amount),
      short_amount: toNumber(row.short_amount),
      excess_amount: toNumber(row.excess_amount),
      dr_amount: toNumber(row.dr_amount),
      cr_amount: toNumber(row.cr_amount),
    })),
    bank_transaction_summary: (data.bank_transaction_detailed || []).map((row) => ({
      transaction_type: row.transaction_type || '',
      partyorreference: row.partyorreference || '',
      dr_amount: toNumber(row.dr_amount),
      cr_amount: toNumber(row.cr_amount),
    })),
    fule_purchase_transactions_summary: (data.fule_purchase_transactions || []).map((row) => ({
      fuel_name: row.fuel_name || '',
      dealer_name: row.dealer_name || '',
      quantity: row.quantity || '',
      invoice_no: row.invoice_no || '',
      amount: toNumber(row.amount),
    })),
    non_fuel_purchase_summary: (data.non_fuel_purchase_detailed || data.non_fuel_purchase_summary || []).map((row) => ({
      product_name: row.product_name || '',
      quantity: row.quantity || '',
      invoice_number: row.invoice_number || '',
      vendor_name: row.name || row.vendor_name || '',
      discount: toNumber(row.discount),
      amount: toNumber(row.amount),
      dr_amount: toNumber(row.dr_amount),
      cr_amount: toNumber(row.cr_amount || row.amount),
    })),
  };
}
